import React, { useEffect, useMemo, useState } from 'react';
import './SecondStep.scss';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import { useProjectWizard } from '../../contexts/ProjectWizardContext';
import { ProjectData } from '../../models/ProjectData';
import { brightDataCountries, isContainText, normalizeKeyword } from '../../../../../shared/utils/utils';
import Loader from '../../../../../shared/components/common/loader/Loader';
import useCanManage from '../../../../../shared/hooks/account/UseCanManage';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../../../shared/components/dropdown-item/DropdownItem';
import { useGetSearchVolume } from '../../../../../shared/api/hooks/project/useGetSearchVolume';
import { useProjectSubmit } from '../../hooks/UseProjectSubmit';

interface CustomQuestion {
  text: string;
  checked: boolean;
  disabled: boolean;
  hasBrandConflict: boolean;
}

const SecondStep: React.FC = () => {
  const [apiError, setApiError] = useState<string | null>(null);
  const { accountResponse } = useAccount();
  const {
    activeStep,
    questionResponse,
    setProjectData,
    setSearchVolume,
    setActiveStep,
    domain,
    setDomain,
    setFirmName,
  } = useProjectWizard();
  const { canManage } = useCanManage();
  const { t } = useTranslation();

  const [brand, setBrand] = useState<string | null>(null);
  const [projectCountry, setProjectCountry] = useState<string | null>(null);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { submitProject } = useProjectSubmit();
  const { fetchSearchVolume, isLoading } = useGetSearchVolume();

  const canShowSearchVolumeStep = Boolean(
    accountResponse && accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin
  );

  useEffect(() => {
    if (questionResponse) {
      const seen = new Set<string>();
      const maxMonitorCount = accountResponse?.maxMonitorCount || 0;
      const createdMonitorCount = accountResponse?.createdMonitorCount || 0;

      let maxSelected = maxMonitorCount - createdMonitorCount;
      const predefinedQuestions = questionResponse.questions
        .filter((question) => {
          const normalized = question.trim().toLowerCase();
          if (seen.has(normalized)) {
            return false;
          }
          seen.add(normalized);
          return true;
        })
        .map((question) => {
          const questionData = {
            text: question,
            checked: maxSelected > 0,
            disabled: maxSelected <= 0,
            hasBrandConflict: false,
          };
          maxSelected = maxSelected - 1;
          return questionData;
        });

      setCustomQuestions(predefinedQuestions);
      setBrand(questionResponse.brand || null);
      setProjectCountry(questionResponse.projectCountry || null);
    }
  }, [questionResponse]);

  const back = () => {
    const step = domain && domain.trim() !== '' ? 'DOMAIN' : 'FIRM_NAME';
    setDomain(null);
    setFirmName(null);
    setActiveStep(step);
  };

  const checkedCustomQuestions = useMemo(
    () => customQuestions.filter((q) => q.checked).map((q) => q.text),
    [customQuestions]
  );

  const allCheckedQuestions = useMemo(() => [...checkedCustomQuestions], [checkedCustomQuestions]);

  const canAddQuestion = useMemo(() => {
    if (!accountResponse) {
      return false;
    }
    const { maxMonitorCount, createdMonitorCount } = accountResponse;
    const checkedCount = allCheckedQuestions.length;
    const limit = maxMonitorCount - createdMonitorCount;

    return canManage && checkedCount < limit;
  }, [accountResponse, allCheckedQuestions]);

  useEffect(() => {
    setCustomQuestions((prevCustomQuestions) =>
      prevCustomQuestions.map((q) => ({
        ...q,
        disabled: !q.checked && !canAddQuestion,
      }))
    );
  }, [canAddQuestion]);

  const onCustomQuestionChange = (index: number, checked: boolean) => {
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index].checked = checked;
      updated[index].disabled = !checked && !canAddQuestion;

      return updated;
    });
  };

  const onCustomQuestionTextChange = (index: number, input: HTMLInputElement) => {
    const value = input.value;
    setApiError(null);
    setCustomQuestions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        text: value,
        hasBrandConflict: isContainText(brand, value) || false,
      };
      return updated;
    });
  };

  const createProjectData = (): ProjectData => {
    const uniqueQuestions: string[] = [];
    const seen = new Set<string>();

    for (const rawQuestion of allCheckedQuestions) {
      const question = rawQuestion.trim();
      if (question !== '' && !seen.has(question)) {
        seen.add(question);
        uniqueQuestions.push(question);
      }
    }

    return {
      ids: questionResponse?.ids || [],
      firmName: brand ? normalizeKeyword(brand) : brand,
      domain,
      questions: uniqueQuestions,
      projectCountry,
    } as ProjectData;
  };

  const addCustomQuestion = () => {
    setApiError(null);
    if (canAddQuestion) {
      setCustomQuestions((prev) => [
        ...prev,
        { text: '', checked: true, disabled: false, hasBrandConflict: false },
      ]);
    }
  };

  const allQuestionsCount = () => {
    const all = accountResponse?.createdMonitorCount || 0;
    const selected = customQuestions.filter((q) => q.checked === true).length;
    return all + selected;
  };

  const renderLimits = () => {
    const countQuestionsWithSelected = allQuestionsCount();
    const max = accountResponse?.maxMonitorCount || 0;
    if (countQuestionsWithSelected > max) {
      return (
        <>
          <span>{Intl.NumberFormat('pl-PL').format(countQuestionsWithSelected)}</span> /{' '}
          {Intl.NumberFormat('pl-PL').format(max)}
        </>
      );
    }

    return (
      <>
        {Intl.NumberFormat('pl-PL').format(countQuestionsWithSelected)} /{' '}
        {Intl.NumberFormat('pl-PL').format(max)}
      </>
    );
  };

  if (brand === null) {
    return <Loader />;
  }

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (activeStep !== 'QUESTIONS') {
      return;
    }

    if (allCheckedQuestions.length === 0) {
      setApiError(t('selectMinimumOne'));
      return;
    }

    setApiError(null);
    setIsSubmitting(true);

    try {
      const projectData = createProjectData();
      setProjectData(projectData);

      if (canShowSearchVolumeStep) {
        const searchVolumeResponses = await fetchSearchVolume({
          ids: projectData.ids,
          questions: projectData.questions,
          firmName: projectData.firmName,
          projectCountry: projectData.projectCountry,
        });
        setSearchVolume(searchVolumeResponses);
        setActiveStep('SEARCH_VOLUME');
      } else {
        await submitProject(projectData);
      }
    } catch (err: any) {
      setApiError(err.response?.data?.error || t('unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="project-wizard__page-box project-wizard__page-box--question">
      {isSubmitting && (
        <Loader
          loadingLabel={canShowSearchVolumeStep ? 'loadingDataSearchVolume' : 'loadingSaveProject'}
        />
      )}

      <div className="project-wizard__form">
        <div className="form-box__row">
          <h2>{t('addCustomQuestions')}:</h2>
          <div className="project-wizard__limits">
            {t('currentLimits')}: {renderLimits()}
          </div>
          {domain && domain.trim() !== '' && (
            <div className="form-box__brand">
              <div className="form__label">{t('foundBrandName')}:</div>
              <InputText defaultValue={brand || ''} onChange={(e) => setBrand(e.target.value)} />
            </div>
          )}
          <div className="form-box__country">
            <div className="form__label">{t('geolocation')}:</div>
            <Dropdown
              value={brightDataCountries.find((country) => country.code === projectCountry) || null}
              onChange={(e) => setProjectCountry(e.value.code)}
              placeholder={t('selectCountry')}
              options={brightDataCountries}
              className="dropdown"
              optionLabel="name"
              itemTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              valueTemplate={(option) => <DropdownItem icon={option.icon} name={option.name} />}
              filter
            />
          </div>
          <ul>
            {customQuestions.map((customQuestion, index) => (
              <li key={`custom-question-${index}`}>
                <div className="list-block">
                  <Checkbox
                    id={`checkbox-question-${index}`}
                    inputId={`custom-question-${index}`}
                    onChange={(e) => onCustomQuestionChange(index, e.checked || false)}
                    checked={customQuestion.checked}
                    disabled={customQuestion.disabled}
                  />
                  <InputText
                    placeholder={`${t('customQuestion')} ${index + 1}`}
                    value={customQuestion.text}
                    onChange={(e) => onCustomQuestionTextChange(index, e.target)}
                  />
                  {customQuestion.disabled && (
                    <Tooltip
                      target={`#checkbox-question-${index}`}
                      content={t('limitRestriction')}
                      className="tooltip--top"
                      position="top"
                      style={{ width: '220px' }}
                      showDelay={5}
                    />
                  )}
                </div>
                {customQuestion.hasBrandConflict && (
                  <>
                    <span className="material-symbols-outlined" id={`warming-icon-${index}`}>
                      warning
                    </span>
                    <Tooltip
                      target={`#warming-icon-${index}`}
                      content={t('brandNameRestriction')}
                      className="tooltip--top"
                      position="top"
                      style={{ width: '220px' }}
                      showDelay={5}
                    />
                  </>
                )}
              </li>
            ))}
            <li className="add-new">
              <div className="list-block">
                <label />
                <Button
                  id="add-new-btn"
                  onClick={addCustomQuestion}
                  type="button"
                  className={`btn btn--link btn--link-black ${
                    canAddQuestion ? '' : 'btn--disabled-tooltip'
                  }`}
                >
                  {t('addCustomQuestions')}
                </Button>
                {!canAddQuestion && (
                  <Tooltip
                    target={`#add-new-btn`}
                    content={t('limitRestriction')}
                    className="tooltip--top"
                    position="top"
                    style={{ width: '220px' }}
                    showDelay={5}
                  />
                )}
              </div>
            </li>
          </ul>

          {apiError && <span className="error-message">{apiError}</span>}
        </div>
      </div>

      <div className="project-wizard__action project-wizard__action--inline">
        <Button onClick={back} className="btn btn--secondary btn--sm" disabled={isSubmitting}>
          {t('back')}
        </Button>
        <Button
          onClick={handleSubmit}
          type="submit"
          className="btn btn--sm"
          disabled={isSubmitting || brand.trim() === ''}
        >
          {t(canShowSearchVolumeStep ? 'next' : 'saveAndLaunch')}
        </Button>
      </div>
    </div>
  );
};

export default SecondStep;
